import { useScrollReveal } from '../../hooks/useScrollReveal';
import { RouteDisplay } from '../ui/RouteDisplay';
import { SectionLabel, StatusDot } from '@perch/ui';

export function RealExampleSection() {
  const heading = useScrollReveal();
  const step1 = useScrollReveal();
  const step2 = useScrollReveal();
  const step3 = useScrollReveal();
  const step4 = useScrollReveal();

  return (
    <section className="relative py-24 px-4 sm:px-6 bg-white dark:bg-stone-900 border-b border-stone-200/60 dark:border-stone-800/50">
      <div className="max-w-4xl mx-auto">
        <div
          ref={heading.ref}
          className={`text-center mb-12 transition-[opacity,transform] duration-500 ease-out ${heading.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
        >
          <SectionLabel className="mb-3">See It in Action</SectionLabel>
          <h2 className="text-3xl sm:text-4xl font-serif text-stone-900 dark:text-stone-100">
            A Real Example
          </h2>
        </div>

        {/* Timeline/Story */}
        <div className="relative">
          {/* Connecting line */}
          <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-stone-200/60 dark:bg-stone-700/50 md:-translate-x-1/2" />

          <div className="space-y-6 md:space-y-0">
            {/* Step 1 - Book */}
            <div
              ref={step1.ref}
              className={`relative flex flex-col md:flex-row md:items-center gap-3 md:gap-8 pl-12 md:pl-0 transition-[opacity,transform] duration-500 ease-out ${step1.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
            >
              <div className="absolute left-0 md:left-1/2 w-8 h-8 md:w-4 md:h-4 bg-stone-900 dark:bg-stone-100 rounded-full md:-translate-x-1/2 border-4 border-white dark:border-stone-900 flex items-center justify-center shadow-sm">
                <span className="text-xs font-bold text-white dark:text-stone-900 md:hidden">1</span>
              </div>
              <div className="md:w-1/2 md:text-right md:pr-12">
                <div className="bg-white dark:bg-stone-800 rounded-2xl p-5 md:p-6 md:inline-block md:ml-auto border border-stone-200/50 dark:border-stone-700/50 shadow-sm text-left">
                  <div className="flex items-center justify-between mb-4">
                    <SectionLabel>Day 1</SectionLabel>
                    <span className="text-xs text-stone-400 dark:text-stone-500">American Airlines</span>
                  </div>
                  <div className="flex items-center justify-center mb-4">
                    <RouteDisplay
                      segments={[{ origin: 'JFK', destination: 'MIA' }]}
                      tripType="round_trip"
                      size="md"
                    />
                  </div>
                  <div className="flex items-center justify-between pt-3 border-t border-stone-100 dark:border-stone-700/50">
                    <span className="text-xs text-stone-400 dark:text-stone-500 font-mono">CONF: XKFJ7M</span>
                    <span className="text-xl font-data tabular-nums text-stone-900 dark:text-stone-100">$287</span>
                  </div>
                </div>
              </div>
              <div className="md:w-1/2 md:pl-12">
                <p className="text-stone-500 dark:text-stone-400 text-sm md:text-base">You book your flight on American. Typical price for this route.</p>
              </div>
            </div>

            {/* Step 2 - We monitor */}
            <div
              ref={step2.ref}
              className={`relative flex flex-col md:flex-row md:items-center gap-3 md:gap-8 md:pt-6 pl-12 md:pl-0 transition-[opacity,transform] duration-500 ease-out ${step2.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
              style={{ transitionDelay: '100ms' }}
            >
              <div className="absolute left-0 md:left-1/2 w-8 h-8 md:w-4 md:h-4 bg-stone-300 dark:bg-stone-600 rounded-full md:-translate-x-1/2 border-4 border-white dark:border-stone-900 flex items-center justify-center shadow-sm">
                <span className="text-xs font-bold text-stone-600 dark:text-stone-300 md:hidden">2</span>
              </div>
              <div className="md:w-1/2 md:text-right md:pr-12 md:order-1">
                <p className="text-stone-500 dark:text-stone-400 text-sm md:text-base">Perch checks this flight automatically, watching for price changes.</p>
              </div>
              <div className="md:w-1/2 md:pl-12 md:order-2">
                <div className="bg-gradient-to-br from-stone-100 to-stone-50 dark:from-stone-800 dark:to-stone-800/80 rounded-2xl p-5 md:p-6 md:inline-block border border-stone-200/50 dark:border-stone-700/50">
                  <SectionLabel className="mb-1">Days 2–14</SectionLabel>
                  <div className="flex items-center gap-2">
                    <StatusDot />
                    <span className="text-stone-600 dark:text-stone-400 font-medium">Watching...</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 3 - Price drop — echoes hero notification card */}
            <div
              ref={step3.ref}
              className={`relative flex flex-col md:flex-row md:items-center gap-3 md:gap-8 md:pt-6 pl-12 md:pl-0 transition-[opacity,transform] duration-500 ease-out ${step3.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
              style={{ transitionDelay: '200ms' }}
            >
              <div className="absolute left-0 md:left-1/2 w-8 h-8 md:w-4 md:h-4 bg-emerald-500 rounded-full md:-translate-x-1/2 border-4 border-white dark:border-stone-900 flex items-center justify-center shadow-sm">
                <span className="text-xs font-bold text-white md:hidden">3</span>
              </div>
              <div className="md:w-1/2 md:text-right md:pr-12">
                <div className="bg-white dark:bg-stone-800 border border-stone-200/60 dark:border-stone-700/50 rounded-2xl p-5 md:p-6 md:inline-block md:ml-auto shadow-lg shadow-stone-900/[0.04] dark:shadow-black/20">
                  <div className="flex items-center gap-2 mb-2">
                    <StatusDot />
                    <SectionLabel variant="emerald">Price Drop Found</SectionLabel>
                  </div>
                  <SectionLabel className="mb-1">Day 15</SectionLabel>
                  <div className="flex items-baseline gap-2">
                    <span className="text-stone-400 dark:text-stone-500 line-through">$287</span>
                    <span className="text-2xl font-data tabular-nums text-emerald-600 dark:text-emerald-400">$218</span>
                  </div>
                </div>
              </div>
              <div className="md:w-1/2 md:pl-12">
                <p className="text-stone-500 dark:text-stone-400 text-sm md:text-base">The same flight is now 24% cheaper.</p>
              </div>
            </div>

            {/* Step 4 - Rebook + trust builder integrated */}
            <div
              ref={step4.ref}
              className={`relative flex flex-col md:flex-row md:items-center gap-3 md:gap-8 md:pt-6 pl-12 md:pl-0 transition-[opacity,transform] duration-500 ease-out ${step4.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
              style={{ transitionDelay: '300ms' }}
            >
              <div className="absolute left-0 md:left-1/2 w-8 h-8 md:w-4 md:h-4 bg-amber-500 rounded-full md:-translate-x-1/2 border-4 border-white dark:border-stone-900 flex items-center justify-center shadow-sm">
                <span className="text-xs font-bold text-white md:hidden">4</span>
              </div>
              <div className="md:w-1/2 md:text-right md:pr-12 md:order-1">
                <p className="text-stone-500 dark:text-stone-400 text-sm md:text-base">We handle the rebooking — American issues a $69 trip credit to your account.</p>
                <p className="text-stone-400 dark:text-stone-500 text-xs mt-2">Most major airlines issue travel credit when you rebook at a lower fare.</p>
              </div>
              <div className="md:w-1/2 md:pl-12 md:order-2">
                <div className="relative overflow-hidden bg-amber-50 dark:bg-amber-900/20 border border-amber-200/60 dark:border-amber-800/50 rounded-2xl p-5 md:p-6 md:inline-block">
                  <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 via-transparent to-amber-500/5 pointer-events-none" />
                  <div className="relative">
                    <SectionLabel variant="amber" className="mb-1">You Saved</SectionLabel>
                    <div className="text-3xl font-data tabular-nums text-stone-900 dark:text-stone-100">$69</div>
                    <div className="text-sm text-stone-500 dark:text-stone-400 mt-1">Same flight, lower price</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
